import { useState, useEffect } from 'react';
import { PlusCircle, Edit2, Trash2, Package, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function IngredientManagement() {
  const [ingredients, setIngredients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(''); // NEW: Branch filter
  const [selectedStockFilter, setSelectedStockFilter] = useState(''); // NEW: Stock/Non-Stock filter
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Stock update modal state
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockModalData, setStockModalData] = useState({ ingredient: null, amount: '' });
  
  // Get current user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [formData, setFormData] = useState({
    name: '',
    unit: 'pcs',
    stock: 0,
    price_per_unit: 0,
    hpp: 0,
    min_stock: 10,
    category: 'snack',
    branch_id: '',
    is_non_stock: false,
    stock_change_reason: ''
  });
  
  const [initialStock, setInitialStock] = useState(null);

  useEffect(() => {
    fetchIngredients();
    fetchBranches();
  }, []);

  const fetchIngredients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ingredients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setIngredients(data.ingredients || []);
    } catch (error) {
      console.error('Error fetching ingredients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setBranches(data.branches);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      // Check if stock has changed and reason is required
      if (editingId && initialStock !== null) {
        const newStock = parseInt(formData.stock) || 0;
        if (newStock !== initialStock && !formData.stock_change_reason.trim()) {
          toast.error('Alasan perubahan stok wajib diisi!', {
            description: 'Masukkan alasan kenapa stok diubah untuk keperluan audit',
            duration: 4000
          });
          return;
        }
      }
      
      const url = editingId 
        ? `${API_URL}/api/ingredients/${editingId}`
        : `${API_URL}/api/ingredients`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      // Prepare data to send
      const dataToSend = { ...formData };
      
      // Remove stock_change_reason if stock hasn't changed
      if (editingId && initialStock !== null && parseInt(formData.stock) === initialStock) {
        delete dataToSend.stock_change_reason;
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        fetchIngredients();
        resetForm();
        toast.success(editingId ? 'Bahan baku berhasil diupdate!' : 'Bahan baku berhasil ditambahkan!', {
          description: editingId ? 'Perubahan telah disimpan' : 'Bahan baku baru telah ditambahkan ke inventory',
          duration: 3000
        });
      } else {
        // Handle error response safely
        let errorMessage = 'Gagal menyimpan bahan baku';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (parseError) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving ingredient:', error);
      toast.error('Gagal menyimpan bahan baku', {
        description: error.message || 'Terjadi kesalahan saat menyimpan data',
        duration: 4000
      });
    }
  };

  const handleEdit = (ingredient) => {
    setEditingId(ingredient.id);
    setFormData({
      name: ingredient.name,
      unit: ingredient.unit,
      stock: ingredient.stock,
      price_per_unit: ingredient.price_per_unit,
      min_stock: ingredient.min_stock,
      category: ingredient.category,
      branch_id: ingredient.branch_id,
      is_non_stock: ingredient.is_non_stock || false,
      stock_change_reason: ''
    });
    // Store initial stock for comparison
    setInitialStock(ingredient.stock);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    // Show toast confirmation with action buttons
    toast.warning('Yakin ingin menghapus bahan baku ini?', {
      description: 'Bahan baku yang dihapus tidak dapat dikembalikan',
      duration: 5000,
      action: {
        label: 'Hapus',
        onClick: async () => {
          try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/ingredients/${id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
              fetchIngredients();
              toast.success('Bahan baku berhasil dihapus!', {
                description: 'Data telah dihapus dari sistem',
                duration: 3000
              });
            } else {
              toast.error('Gagal menghapus bahan baku', {
                description: 'Terjadi kesalahan saat menghapus data',
                duration: 4000
              });
            }
          } catch (error) {
            console.error('Error deleting ingredient:', error);
            toast.error('Gagal menghapus bahan baku', {
              description: error.message || 'Terjadi kesalahan pada server',
              duration: 4000
            });
          }
        }
      }
    });
  };

  const openStockModal = (ingredient) => {
    setStockModalData({ ingredient, amount: '' });
    setShowStockModal(true);
  };

  const handleStockUpdate = async () => {
    const { ingredient, amount } = stockModalData;
    const change = parseInt(amount);
    
    if (!change || isNaN(change)) {
      toast.error('Jumlah tidak valid', {
        description: 'Masukkan angka yang valid',
        duration: 3000
      });
      return;
    }

    if (change === 0) {
      toast.error('Jumlah harus lebih dari 0', {
        description: 'Masukkan jumlah yang akan ditambahkan',
        duration: 3000
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ingredients/${ingredient.id}/stock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quantity_change: change,
          reason: change > 0 ? 'restock' : 'manual_adjustment'
        })
      });

      if (response.ok) {
        fetchIngredients();
        setShowStockModal(false);
        setStockModalData({ ingredient: null, amount: '' });
        toast.success('Stok berhasil diperbarui!', {
          description: `Stok ${ingredient.name} bertambah ${change} ${ingredient.unit}`,
          duration: 3000
        });
      } else {
        toast.error('Gagal memperbarui stok', {
          description: 'Terjadi kesalahan saat memperbarui stok',
          duration: 4000
        });
      }
    } catch (error) {
      console.error('Error updating stock:', error);
      toast.error('Gagal memperbarui stok', {
        description: error.message || 'Terjadi kesalahan pada server',
        duration: 4000
      });
    }
  };

  // Filter ingredients based on search and branch
  const filteredIngredients = ingredients.filter(ingredient => {
    const matchesSearch = ingredient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ingredient.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Branch filter logic
    let matchesBranch = true;
    if (selectedBranchFilter) {
      if (selectedBranchFilter === 'global') {
        // Show ingredients without branch_id (global ingredients)
        matchesBranch = !ingredient.branch_id;
      } else {
        // Show ingredients with specific branch_id
        matchesBranch = ingredient.branch_id === selectedBranchFilter;
      }
    }
    
    // Stock/Non-Stock filter logic
    let matchesStockType = true;
    if (selectedStockFilter) {
      if (selectedStockFilter === 'stock') {
        matchesStockType = !ingredient.is_non_stock;
      } else if (selectedStockFilter === 'non-stock') {
        matchesStockType = ingredient.is_non_stock === true;
      }
    }
    
    return matchesSearch && matchesBranch && matchesStockType;
  });

  // Pagination calculations (on filtered results)
  const totalPages = Math.ceil(filteredIngredients.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentIngredients = filteredIngredients.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedBranchFilter, selectedStockFilter]);

  const resetForm = () => {
    setFormData({
      name: '',
      unit: 'pcs',
      stock: 0,
      price_per_unit: 0,
      min_stock: 10,
      category: 'snack',
      branch_id: '',
      is_non_stock: false,
      stock_change_reason: ''
    });
    setEditingId(null);
    setShowForm(false);
    setInitialStock(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Kelola Bahan Baku</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <PlusCircle className="h-5 w-5" />
          Tambah Bahan Baku
        </button>
      </div>

      {/* Form Modal - Pop-up */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-blue-50 border-b border-blue-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-bold text-gray-900">
                {editingId ? 'Edit Bahan Baku' : 'Tambah Bahan Baku Baru'}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700 transition"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Bahan
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Satuan
              </label>
              <select
                value={formData.unit}
                onChange={(e) => setFormData({...formData, unit: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="pcs">Pcs</option>
                <option value="gram">Gram</option>
                <option value="ml">ML</option>
                <option value="pack">Pack</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {editingId ? 'Stok Saat Ini' : 'Stok Awal'}
              </label>
              <input
                type="number"
                value={formData.stock}
                onChange={(e) => setFormData({...formData, stock: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
                required
              />
              {editingId && initialStock !== null && parseInt(formData.stock) !== initialStock && (
                <p className="text-xs text-amber-600 mt-1 font-medium">
                   Stok akan berubah dari {initialStock} ke {formData.stock}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Harga per Satuan
              </label>
              <input
                type="number"
                value={formData.price_per_unit}
                onChange={(e) => setFormData({...formData, price_per_unit: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                HPP (Harga Pokok Penjualan)
              </label>
              <input
                type="number"
                value={formData.hpp || ''}
                onChange={(e) => setFormData({...formData, hpp: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
                placeholder="Opsional - akan otomatis dihitung dari pembelian"
              />
              <p className="text-xs text-gray-500 mt-1">
                 Kosongkan jika HPP akan dihitung otomatis dari pembelian
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min. Stok (Alert)
              </label>
              <input
                type="number"
                value={formData.min_stock}
                onChange={(e) => setFormData({...formData, min_stock: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategori
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="snack">Snack</option>
                <option value="minuman">Minuman</option>
                <option value="makanan">Makanan</option>
                <option value="lainnya">Lainnya</option>
              </select>
            </div>
            
            {/* Stock Change Reason - Show only when editing and stock changed */}
            {editingId && initialStock !== null && parseInt(formData.stock) !== initialStock && (
              <div className="col-span-2">
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
                  <label className="block text-sm font-bold text-amber-900 mb-2">
                     Alasan Perubahan Stok (WAJIB) *
                  </label>
                  <textarea
                    required
                    value={formData.stock_change_reason}
                    onChange={(e) => setFormData({...formData, stock_change_reason: e.target.value})}
                    className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    rows="2"
                    placeholder="Contoh: Koreksi stok fisik, Bahan rusak/kadaluarsa, Restock dari supplier, dll"
                  />
                  <p className="text-xs text-amber-700 mt-1">
                     Alasan ini akan dicatat dalam audit trail untuk keperluan tracking dan akuntabilitas
                  </p>
                </div>
              </div>
            )}

            {/* Non-Stock Item Checkbox */}
            <div className="col-span-2">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_non_stock}
                    onChange={(e) => setFormData({...formData, is_non_stock: e.target.checked})}
                    className="mt-1 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-gray-900"> Non-Stock Item (Consumable)</span>
                      <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded">Optional</span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Centang ini untuk bahan yang <strong>tidak perlu tracking stock</strong> (contoh: es batu, margarin, bumbu). 
                      Pembelian bahan ini akan langsung masuk ke <strong>Biaya Operasional</strong>, bukan COGS.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Branch Selector - Only for Admin/Supervisor */}
            {(user.role === 'admin' || user.role === 'supervisor') && branches.length > 0 && (
              <div className="col-span-2 border-t pt-4 mt-2">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-blue-900 mb-1"> Assign Cabang</h4>
                      <p className="text-xs text-blue-800">
                        Pilih cabang untuk bahan baku ini. Stock akan terpisah per cabang.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cabang *
                  </label>
                  <select
                    required
                    value={formData.branch_id}
                    onChange={(e) => setFormData({...formData, branch_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Pilih Cabang</option>
                    {branches.map((branch) => (
                      <option key={branch.branch_id} value={branch.branch_id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                     Bahan baku ini akan terdaftar di cabang yang dipilih
                  </p>
                </div>
              </div>
            )}

            <div className="col-span-2 flex gap-2 justify-end sticky bottom-0 bg-white pt-4 border-t border-gray-200 -mx-6 px-6 pb-0">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {editingId ? 'Update' : 'Tambah'}
              </button>
            </div>
          </form>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Bahan</p>
              <p className="text-3xl font-bold text-gray-900">{ingredients.length}</p>
            </div>
            <Package className="h-10 w-10 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Filtered</p>
              <p className="text-3xl font-bold text-gray-900">{filteredIngredients.length}</p>
              {selectedBranchFilter && (
                <p className="text-xs text-blue-600 mt-1">
                  {selectedBranchFilter === 'global' ? ' Global' : ` ${branches.find(b => b.branch_id === selectedBranchFilter)?.name}`}
                </p>
              )}
            </div>
            <Package className="h-10 w-10 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Stok Rendah</p>
              <p className="text-3xl font-bold text-gray-900">
                {ingredients.filter(i => !i.is_non_stock && i.stock < i.min_stock).length}
              </p>
            </div>
            <AlertTriangle className="h-10 w-10 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="mb-4 flex gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Cari bahan baku berdasarkan nama atau kategori..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        <select
          value={selectedStockFilter}
          onChange={(e) => setSelectedStockFilter(e.target.value)}
          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
        >
          <option value=""> Semua Tipe</option>
          <option value="stock"> Stock</option>
          <option value="non-stock"> Non-Stock</option>
        </select>
        
        <select
          value={selectedBranchFilter}
          onChange={(e) => setSelectedBranchFilter(e.target.value)}
          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[180px]"
        >
          <option value=""> Semua Cabang</option>
          <option value="global"> Global (Tanpa Cabang)</option>
          {branches.map((branch) => (
            <option key={branch.branch_id} value={branch.branch_id}>
              {branch.name}
            </option>
          ))}
        </select>
      </div>
      
      {(searchTerm || selectedBranchFilter) && (
        <p className="text-sm text-gray-600 mb-4">
          Ditemukan <span className="font-semibold">{filteredIngredients.length}</span> hasil
          {searchTerm && ` untuk "${searchTerm}"`}
          {selectedBranchFilter && ` di ${selectedBranchFilter === 'global' ? 'Global' : branches.find(b => b.branch_id === selectedBranchFilter)?.name}`}
        </p>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nama</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cabang</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stok</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">HPP</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Harga/Unit</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aksi</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentIngredients.map((ingredient) => {
              // Non-stock items should not have low stock warning
              const isNonStock = ingredient.is_non_stock;
              const isLowStock = !isNonStock && ingredient.stock <= ingredient.min_stock;
              
              return (
                <tr key={ingredient.id} className={isLowStock ? 'bg-red-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {isLowStock && <AlertTriangle className="h-4 w-4 text-red-500" />}
                      {isNonStock && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded">
                           Non-Stock
                        </span>
                      )}
                      <span className="font-medium text-gray-900">{ingredient.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {ingredient.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {ingredient.branch_id ? (
                        <div className="flex items-center gap-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-100 text-blue-800 text-xs font-medium">
                             {branches.find(b => b.branch_id === ingredient.branch_id)?.name || 'Unknown'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Global</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isNonStock ? (
                      <span className="text-xs text-gray-400 italic">Not tracked</span>
                    ) : (
                      <span className={`font-medium ${isLowStock ? 'text-red-600' : 'text-gray-900'}`}>
                        {ingredient.stock} {ingredient.unit}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {ingredient.hpp && ingredient.hpp > 0 ? (
                      <span className="font-medium text-green-700">{formatCurrency(ingredient.hpp)}</span>
                    ) : (
                      <span className="text-gray-400 italic text-xs">Belum ada</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(ingredient.price_per_unit)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(ingredient)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(ingredient.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {ingredients.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Belum ada bahan baku</p>
          </div>
        )}

        {/* Pagination */}
        {ingredients.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t mt-4">
            <div className="text-sm text-gray-700">
              Menampilkan <span className="font-medium">{indexOfFirstItem + 1}</span> sampai{' '}
              <span className="font-medium">{Math.min(indexOfLastItem, ingredients.length)}</span> dari{' '}
              <span className="font-medium">{ingredients.length}</span> bahan baku
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <div className="flex items-center gap-1">
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          currentPage === pageNumber
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber} className="px-2 text-gray-500">...</span>;
                  }
                  return null;
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stock Update Modal */}
      {showStockModal && stockModalData.ingredient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Tambah Stok: {stockModalData.ingredient.name}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stok Saat Ini
              </label>
              <div className="text-2xl font-bold text-gray-900">
                {stockModalData.ingredient.stock} {stockModalData.ingredient.unit}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jumlah Tambahan *
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  value={stockModalData.amount}
                  onChange={(e) => setStockModalData({ ...stockModalData, amount: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleStockUpdate();
                    }
                  }}
                  placeholder="Masukkan jumlah"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
                <span className="absolute right-3 top-2 text-gray-500 text-sm">
                  {stockModalData.ingredient.unit}
                </span>
              </div>
              {stockModalData.amount && parseInt(stockModalData.amount) > 0 && (
                <p className="mt-2 text-sm text-gray-600">
                  Stok baru akan menjadi: <span className="font-semibold text-green-600">
                    {stockModalData.ingredient.stock + parseInt(stockModalData.amount)} {stockModalData.ingredient.unit}
                  </span>
                </p>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowStockModal(false);
                  setStockModalData({ ingredient: null, amount: '' });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Batal
              </button>
              <button
                onClick={handleStockUpdate}
                disabled={!stockModalData.amount || parseInt(stockModalData.amount) <= 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Tambah Stok
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
